import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Space,
  Typography,
  message
} from 'antd';
import { SettingOutlined, SaveOutlined } from '@ant-design/icons';
import { useLanguage } from '../../contexts/LanguageContext';
import FieldDisplaySettingTable from '../FieldDisplaySettingTable';

const { Text } = Typography;

// 可作為「填充值」的 Meta Flow 輸入型組件 type 列表。
// 與 EFormPivotReport / WebhookFlowResponse 對齊：這些 type 的 name 會出現在 response_json key 裡。
const INPUT_COMPONENT_TYPES = new Set([
  'text_input',
  'text_area',
  'date_picker',
  'calendar_picker',
  'time_picker',
  'select',
  'checkbox',
  'radio',
  'chips_selector',
  'photo_picker',
  'document_picker',
  'opt_in'
]);

/**
 * 從 Meta Flow 編輯器的 screens 結構掃描出輸入型欄位。
 * 與 [src/components/EFormDesigner/EFormFieldDisplaySetting.js](../EFormDesigner/EFormFieldDisplaySetting.js)
 * 不同：Meta Flow 沒有 HTML，欄位資訊存在 `screen.data.actions[].name / title` 上，
 * 這個 name 也是 WhatsApp 提交回來 response_json 的 key。
 */
const scanMetaFlowFields = (screens, t) => {
  if (!Array.isArray(screens) || screens.length === 0) return [];

  const fields = [];
  const seen = new Set();

  screens.forEach((screen, screenIndex) => {
    const actions = screen?.data?.actions || [];
    actions.forEach((action) => {
      if (!action || !INPUT_COMPONENT_TYPES.has(action.type)) return;

      const fieldId = action.name || action.id;
      if (!fieldId || seen.has(fieldId)) return;
      seen.add(fieldId);

      const label =
        action.title ||
        action.label ||
        action.data?.label ||
        fieldId ||
        t('eformDesigner.fieldDisplaySetting.messages.unnamedField');

      // 對 text_input 用 input_type（text/number/email/...），
      // 其他組件直接用 type，讓 EFormPivotReport 的 numeric 判斷可重用。
      const inputType = action.data?.input_type || action.type;

      fields.push({
        rowKey: fieldId,
        fieldId,
        fieldType: action.type,
        inputType,
        originalInputType: inputType, // 切換 JSON 模式後可以還原
        originalLabel: label,
        displayLabel: label,
        showInList: true,
        order: fields.length,
        jsonKey: '',
        screenId: screen?.id,
        screenIndex
      });
    });
  });

  return fields;
};

const MetaFlowFieldDisplaySetting = ({
  visible,
  onClose,
  screens,
  onSave,
  initialSettings = []
}) => {
  const { t } = useLanguage();
  const [fieldSettings, setFieldSettings] = useState([]);
  const [loading, setLoading] = useState(false);

  // 重掃 + merge：fieldId 作為 key，保留使用者改過的 displayLabel / showInList / order / JSON Key 子列。
  // - 掃描出來的欄位用 fieldId 對應 initialSettings；JSON 子列以 rowKey 區分。
  // - 已被刪掉的組件就自然消失；新組件以 showInList=false 加入。
  useEffect(() => {
    if (!visible) return;

    const scanned = scanMetaFlowFields(screens, t);
    const scannedById = new Map(scanned.map((f) => [f.fieldId, f]));

    if (initialSettings && initialSettings.length > 0) {
      const seenRowKeys = new Set();
      const merged = initialSettings.map((s) => {
        const scannedField = scannedById.get(s.fieldId);
        const isJson = (s.inputType || '') === 'json' || !!s.jsonKey;
        const rowKey = s.rowKey || (isJson && s.jsonKey ? `${s.fieldId}::${s.jsonKey}` : s.fieldId);
        seenRowKeys.add(rowKey);
        return {
          fieldType: scannedField?.fieldType || s.fieldType || '',
          originalInputType: scannedField?.inputType || s.originalInputType || s.inputType || '',
          originalLabel: scannedField?.originalLabel || s.originalLabel || s.fieldId,
          fieldId: s.fieldId,
          inputType: s.inputType || scannedField?.inputType || '',
          jsonKey: s.jsonKey || '',
          displayLabel: s.displayLabel || s.originalLabel || s.fieldId,
          showInList: !!s.showInList,
          order: typeof s.order === 'number' ? s.order : 0,
          rowKey,
          screenId: scannedField?.screenId ?? s.screenId,
          screenIndex: scannedField?.screenIndex ?? s.screenIndex
        };
      });

      // 新掃描到但 settings 沒有的 fieldId，預設不顯示
      scanned.forEach((f) => {
        const fieldHasAnyRow = merged.some((r) => r.fieldId === f.fieldId);
        if (!fieldHasAnyRow) {
          merged.push({ ...f, showInList: false });
        }
      });

      merged.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      merged.forEach((r, i) => { r.order = i; });

      setFieldSettings(merged);
    } else {
      setFieldSettings(
        scanned.map((field) => ({ ...field, showInList: false }))
      );
    }
  }, [visible, screens, initialSettings, t]);

  const handleSave = async () => {
    setLoading(true);
    try {
      // 只回傳 FieldDisplaySetting 規格內欄位（screenId/screenIndex/originalInputType 是純前端顯示用），避免污染後端 model。
      const cleaned = fieldSettings.map(({ screenId, screenIndex, originalInputType, ...rest }) => rest);
      await onSave(cleaned);
      message.success(t('eformDesigner.fieldDisplaySetting.messages.saveSuccess'));
      onClose();
    } catch (error) {
      message.error(t('eformDesigner.fieldDisplaySetting.messages.saveFailed') + (error?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <span>{t('eformDesigner.fieldDisplaySetting.modalTitle')}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width="92%"
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('eformDesigner.fieldDisplaySetting.buttons.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSave}
        >
          {t('eformDesigner.fieldDisplaySetting.buttons.confirm')}
        </Button>
      ]}
    >
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          {t('eformDesigner.fieldDisplaySetting.helperTextNoJson')
            || t('eformDesigner.fieldDisplaySetting.helperText')}
        </Text>
      </div>

      <FieldDisplaySettingTable
        value={fieldSettings}
        onChange={setFieldSettings}
        showScreenColumn={true}
        supportJson={false}
      />
    </Modal>
  );
};

export default MetaFlowFieldDisplaySetting;
